import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import config from 'config';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import useIsFaceVerificationNeeded from '@/hooks/useFaceVerification/useIsFaceVerificationNeeded';
import { useUserContext } from '@/modules/auth/hooks/useUser';

import { ImageVerificationResponse, UseFaceVerificationProps } from './types';
import { verifyFace } from './utils';

const useFaceVerification = ({ entityId }: UseFaceVerificationProps) => {
  const [verificationStatus, setVerificationStatus] = useState<null | ImageVerificationResponse>(
    null,
  );
  const [isCameraReady, setIsCameraReady] = useState<boolean>(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isFaceVerificationModelOpen, { toggle: setIsFaceVerificationModalOpen }] =
    useToggle(false);
  const [isRetryDialogOpen, { toggle: setIsRetryDialogOpen }] = useToggle(false);
  const [noOfAttenptsLeft, setNoOfAttemptsLeft] = useState(3);
  const [backgroundVerification, setBackgroundVerification] = useState(false);
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const user = useUserContext();
  const { federationId, uniqueId } = user;
  const userUniqueId = federationId ?? uniqueId;

  const isVerificationNeeded = useIsFaceVerificationNeeded();

  const faceVerificationInput = useMemo(() => {
    return {
      userUniqueId,
      entityId,
    };
  }, [userUniqueId, entityId]);

  const captureImageFromVideo = useCallback((): string | null => {
    let canvas = canvasRef.current;

    if (!canvas) {
      canvas = document.createElement('canvas');
      canvasRef.current = canvas;
    }

    const context = canvas.getContext('2d');

    if (videoRef.current && videoRef.current.videoWidth && videoRef.current.videoHeight) {
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;
      canvas.width = videoWidth;
      canvas.height = videoHeight;

      if (context) {
        context.translate(canvas.width, 0);
        context.scale(-1, 1);
        context.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

        return canvas.toDataURL('image/jpeg');
      } else {
        console.error('Failed to get canvas context');
      }
    } else {
      console.error('Video dimensions are not available');
      return null;
    }

    return null;
  }, []);

  const allowAccess = useMemo(() => {
    if (!isVerificationNeeded) {
      return true;
    }
    return verificationStatus?.success;
  }, [isVerificationNeeded, verificationStatus]);

  const verifyImage = useCallback(
    async (base64: string) => {
      try {
        const result = (await verifyFace({
          url: config.ecom_face_recognition_url,
          data: { ...faceVerificationInput, base64 },
          setIsFaceVerificationModalOpen,
          setNoOfAttemptsLeft,
          setIsRetryDialogOpen,
          setBackgroundVerification,
          t,
          showSnackbar,
          backgroundVerification,
        })) as ImageVerificationResponse;

        setVerificationStatus(result);
        return result;
      } catch (error) {
        console.error('Error verifying image:', error);
        setVerificationStatus({ success: false, message: 'Verification failed' });
        return { success: false, message: 'Verification failed' };
      }
    },
    [
      faceVerificationInput,
      config.ecom_face_recognition_url,
      setIsFaceVerificationModalOpen,
      setNoOfAttemptsLeft,
      setIsRetryDialogOpen,
      setBackgroundVerification,
      showSnackbar,
      backgroundVerification,
    ],
  );
  const createVideoElement = useCallback(() => {
    if (!videoRef.current) {
      const videoElement = document.createElement('video');
      videoElement.setAttribute('playsInline', '');
      videoElement.setAttribute('autoPlay', '');
      videoElement.muted = true;
      videoElement.className = 'w-full h-full object-cover';
      videoRef.current = videoElement;
    }
    return videoRef.current;
  }, []);
  const startCamera = useCallback(async () => {
    try {
      if (isVerificationNeeded && !isCameraReady) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: 640, height: 480 },
        });
        const videoElement = createVideoElement();

        videoElement.srcObject = stream;
        videoElement.style.transform = 'scaleX(-1)';

        setIsCameraReady(true);
        setIsFaceVerificationModalOpen(true);
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      setIsCameraReady(false);
      setIsFaceVerificationModalOpen(true);
      setVerificationStatus({ success: false, message: 'Camera access denied or unavailable' });
    }
  }, [isVerificationNeeded]);

  const startImageCaptureInterval = useCallback(() => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      if (isCameraReady) {
        const base64Image = captureImageFromVideo();
        if (base64Image) verifyImage(base64Image);
      }
    }, 60000);
  }, [isCameraReady, captureImageFromVideo, verifyImage]);

  const stopImageCaptureInterval = useCallback(() => {
    if (intervalRef.current) {
      if (videoRef.current) {
        document.body.removeChild(videoRef.current);
        videoRef.current.style.display = '';
      }
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (isVerificationNeeded && !isCameraReady) {
      startCamera();
    }
    return () => {
      stopImageCaptureInterval();
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        const tracks = stream.getTracks();
        tracks.forEach((track) => track.stop());
      }
    };
  }, [startCamera, stopImageCaptureInterval, isVerificationNeeded, isCameraReady]);

  useEffect(() => {
    if (isCameraReady && backgroundVerification && !intervalRef.current) {
      const handlePlaying = () => {
        if (videoRef.current) {
          videoRef.current.style.display = 'none';
          document.body.appendChild(videoRef.current);
          videoRef.current?.play();
          startImageCaptureInterval();
        }
      };
      handlePlaying();
    } else if (!backgroundVerification && intervalRef.current) {
      stopImageCaptureInterval();
    }
  }, [isCameraReady, startImageCaptureInterval, backgroundVerification, stopImageCaptureInterval]);

  useEffect(() => {
    if (videoRef.current && canvasRef.current) {
      const { videoWidth, videoHeight } = videoRef.current;
      canvasRef.current.width = videoWidth;
      canvasRef.current.height = videoHeight;
    }
  }, [isCameraReady]);

  return {
    verificationStatus,
    videoRef,
    canvasRef,
    isCameraReady,
    captureImageFromVideo,
    verifyImage,
    isFaceVerificationModelOpen,
    noOfAttenptsLeft,
    isRetryDialogOpen,
    setIsRetryDialogOpen,
    setIsFaceVerificationModalOpen,
    allowAccess,
  };
};

export default useFaceVerification;
